import React, { Component } from 'react'
import { observer, inject } from 'mobx-react'
import { reaction, observable } from 'mobx'
import PropTypes from 'prop-types'
import get from 'lodash/get'
import { NULL as BlockModelNULL } from '@client/components/Block/model'

import ButtonModel, { NULL as ButtonModelNULL } from './model'
import inlineStyles from './inlineStyles'
import findAncestor from '@client/utils/findAncestor'
import EditorModel, {
  NULL as EditorModelNULL,
} from '@client/components/Editor/model'
import { defaultSettings } from './constants'
import AppModel, { NULL as AppModelNULL } from '@client/components/App/model'
import { getPaddingTotal } from '../../Block/component'
import OutlookButton, {
  getOutlookButtonProps,
} from '@client/components/OutlookButton'

const getPaddingReduction = ({ kind, section, columnIndex, fullWidth }) => {
  if (fullWidth) {
    return 0
  }
  return getPaddingTotal({ kind, section, columnIndex, fullWidth })
}

export class Button extends Component {
  @observable editingSettings = BlockModelNULL

  static propTypes = {
    model: PropTypes.instanceOf(ButtonModel),
    editor: PropTypes.instanceOf(EditorModel),
    app: PropTypes.instanceOf(AppModel),
    serverRender: PropTypes.bool,
  }

  static defaultProps = {
    model: ButtonModelNULL,
    editor: EditorModelNULL,
    app: AppModelNULL,
    serverRender: false,
  }

  disposeReaction = null

  constructor(props) {
    super(props)

    this.editingSettings = this.props.app?.editingSettings || BlockModelNULL

    this.disposeReaction = reaction(
      () => this.editingSettings,
      (values) => {
        this.setContainerDimentions()
      }
    )
  }

  componentDidMount() {
    this.column = findAncestor(this.dom, 'column')
    this.setContainerDimentions()
    if (this.column) {
      this.resizeObserver = new ResizeObserver(() =>
        this.setContainerDimentions()
      )
      this.resizeObserver.observe(this.column)
    }

    if (this.props.model.settings.values.text === defaultSettings.text) {
      const formatMessage = this.props.app.formatMessage
      this.props.model.setText(formatMessage(defaultSettings.text))
    }
  }

  componentDidUpdate(prevProps, prevState) {
    this.editingSettings = this.props.app?.editingSettings || BlockModelNULL
  }

  componentWillUnmount() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
    }
    if (this.disposeReaction) {
      this.disposeReaction()
    }
  }

  calculateStyles = (settings, globalSettings) => {
    const backgroundColor =
      settings.backgroundColor || globalSettings.button_backgroundColor
    const height = settings.height || globalSettings.button_height
    const fontSize = settings.fontSize || globalSettings.button_fontSize
    const leftRightPadding = 10
    const width = `${
      +(settings.width || globalSettings.button_width) - 2 * leftRightPadding
    }px`
    return {
      width,
      fontSize: fontSize + 'px',
      borderRadius:
        (settings.borderRadius || globalSettings.button_borderRadius) + 'px',
      backgroundColor,
      color: settings.textColor || globalSettings.button_textColor,
      border: 'none',
      padding: `${
        (height - Math.floor(13 * 1.5) - 2) / 2
      }px ${leftRightPadding}px`,
      maxWidth: width,
      maxHeight: `${height}px`,
    }
  }

  setContainerDimentions() {
    if (!this.column) {
      return
    }
    const section = get(this.props, 'model.section')
    const columnIndex = this.props.model.columnIndex
    const kind = this.props.model.kind
    const fullWidth = this.props.model.settings.values.fullWidth

    const padding = getPaddingReduction({
      kind,
      section,
      columnIndex,
      fullWidth,
    })

    const globalSettings = this.props.editor.settings.values
    const borderBoxMaxWidthReduction =
      get(this.props, 'model.section.borderBoxMaxWidthReduction') || 0
    this.props.model.setContainerDimensions({
      button_width: globalSettings.button_width,
      width: this.column.clientWidth - padding - borderBoxMaxWidthReduction,
    })
  }

  render() {
    const globalSettings = this.props.editor.settings.values
    const settings = this.props.model.settings.values
    const styles = {
      ...inlineStyles.a,
      ...this.calculateStyles(settings, globalSettings),
      fontFamily: globalSettings.normalText_fontFamily,
    }
    const formatMessage = this.props.app.formatMessage

    const text =
      defaultSettings.text === settings.text
        ? formatMessage(defaultSettings.text)
        : settings.text || ''

    const calculatedSettings = this.calculateStyles(settings, globalSettings)
    const height =
      parseFloat(settings.height || globalSettings.button_height) || 40
    const width =
      parseFloat(settings.width || globalSettings.button_width) || 200

    const borderStyle =
      settings.borderStyle || globalSettings.button_borderStyle
    const borderColor =
      settings.borderColor || globalSettings.button_borderColor
    const borderWidth =
      settings.borderWidth || globalSettings.button_borderWidth

    const href = settings.url || defaultSettings.url

    return (
      <OutlookButton
        serverRender={this.props.serverRender}
        {...getOutlookButtonProps({
          settings,
          globalSettings,
          width,
          height,
          href: href || '#tbd',
          text,
        })}
      >
        <table
          border="0"
          cellSpacing="0"
          cellPadding="0"
          role="presentation"
          height={height}
          width={width}
          style={{
            lineHeight: '100%',
            borderSpacing: '0px',
            msoPaddingAlt: '0px',
            msoCellspacing: '0px',
            height: `${height}px`,
            width: `${width}px`,
          }}
          className={`btn-container ${this.props.serverRender ? '' : 'button'}`}
          ref={(el) => (this.dom = el)}
        >
          <tbody border="0" cellSpacing="0" cellPadding="0">
            <tr>
              <td
                align="center"
                className="btn-container"
                style={{
                  border: !!borderWidth
                    ? `${borderWidth}px ${borderStyle} ${borderColor}`
                    : 'none',
                  borderRadius: calculatedSettings.borderRadius,
                  cursor: 'auto',
                  background:
                    settings.backgroundColor ||
                    globalSettings.button_backgroundColor,
                  width: `${width}px`,
                  height: `${height}px`,
                  overflow: 'hidden',
                  maxWidth: `${width}px`,
                  maxHeight: `${height}px`,
                }}
                bgcolor={
                  settings.backgroundColor ||
                  globalSettings.button_backgroundColor
                }
                width={width}
                height={height}
                role="presentation"
                valign="middle"
              >
                <a
                  className="btn"
                  href={href || '#tbd'}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={styles}
                  onClick={(ev) => {
                    if (!href) {
                      ev.preventDefault()
                    }
                  }}
                >
                  {!!text ? (
                    <span
                      style={{
                        color:
                          settings.textColor || globalSettings.button_textColor,
                      }}
                    >
                      {text}
                    </span>
                  ) : (
                    <span
                      style={{
                        color:
                          settings.textColor || globalSettings.button_textColor,
                      }}
                      dangerouslySetInnerHTML={{
                        __html: '&nbsp;',
                      }}
                    />
                  )}
                </a>
              </td>
            </tr>
          </tbody>
        </table>
      </OutlookButton>
    )
  }
}

export default inject('editor', 'app', 'serverRender')(observer(Button))
